"use client";

import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import type { CategoryTreeItems } from "@/lib/categories";
import { cn } from "@/lib/utils";
import { YnsLink } from "@/ui/yns-link";

interface CategorySidebarProps {
	categories: CategoryTreeItems;
	currentCategorySlug?: string;
	className?: string;
}

interface CategoryItemProps {
	category: CategoryTreeItems[number];
	currentCategorySlug?: string;
	level: number;
}

const CategoryItem = ({ category, currentCategorySlug, level }: CategoryItemProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const hasChildren = category.children.length > 0;
	const isCurrentCategory = currentCategorySlug === category.slug;

	return (
		<li className="w-full">
			<div className="flex items-center w-full" style={{ marginLeft: `${level * 12}px` }}>
				{hasChildren && (
					<button
						type="button"
						onClick={() => setIsExpanded(!isExpanded)}
						className="flex items-center justify-center size-8 hover:bg-gray-100 rounded-sm transition-colors"
					>
						{isExpanded ? (
							<ChevronDownIcon className="size-4 text-gray-500" />
						) : (
							<ChevronRightIcon className="size-4 text-gray-500" />
						)}
					</button>
				)}
				{!hasChildren && <div className="w-8" />}

				<YnsLink
					href={`/category/${category.slug}`}
					className={cn(
						"flex-1 px-2 py-1.5 text-sm hover:bg-gray-50 rounded-sm transition-colors block",
						isCurrentCategory && "bg-gray-100 text-gray-900 font-medium",
						level > 0 && "text-gray-600",
					)}
				>
					{category.name}
				</YnsLink>
			</div>

			{hasChildren && isExpanded && (
				<ul className="mt-1 space-y-1">
					{category.children.map((child) => (
						<CategoryItem
							key={child.id}
							category={child}
							currentCategorySlug={currentCategorySlug}
							level={level + 1}
						/>
					))}
				</ul>
			)}
		</li>
	);
};

export const CategorySidebar = ({ categories, currentCategorySlug, className }: CategorySidebarProps) => {
	if (categories.length === 0) {
		return (
			<div className={cn("p-4 text-sm text-gray-500", className)}>
				<p>No categories available</p>
			</div>
		);
	}

	return (
		<nav className={cn("p-4", className)}>
			<h3 className="text-sm font-semibold text-gray-900 mb-3">Categories</h3>
			<ul className="space-y-1">
				{categories.map((category) => (
					<CategoryItem
						key={category.id}
						category={category}
						currentCategorySlug={currentCategorySlug}
						level={0}
					/>
				))}
			</ul>
		</nav>
	);
};
